import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  fieldKeys,
  sleepingOptions,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  SecondaryButton,
  Button,
  H2,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  fetchReviews,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
  handleToggleFavorites,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import css from './ListingPage.module.css';
import RelatedListing from './RealatedListing.js';
import CustomerReview from './CustomerReview.js';
import BrandIconCard from '../../components/BrandIconCard/BrandIconCard.js';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck.js';
import { displayPrice } from '../../util/configHelpers.js';
import { formatMoney } from '../../util/currency.js';
import classNames from 'classnames';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (openPopup) {
      document.body.classList.add('disableScroll');
    } else {
      document.body.classList.remove('disableScroll');
    }
  }, [openPopup]);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    relatedListings,
    relatedListingsError,
    featuredListings,
    onUpdateFavorites,
    onFetchlistingReviews,
    relatedListingsReviews,
    marketplaceCurrency,
    titleDesktop,
    titleClassName,
    ...restOfProps
  } = props;
  const [copied, setCopied] = useState(false);
  const validListingTypes = config.listing.listingTypes;
  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const {
    room_arrangements,
    bathroom_facilities,
    meals_kitchen,
    power_lighting,
    laundry_cleaning,
  } = publicData || {};

  const sleepingOptionsMap = sleepingOptions?.reduce((acc, curr) => {
    acc[curr?.value] = curr?.label;
    return acc;
  }, {});
  const sleepingArrangementLabel = sleepingOptionsMap[room_arrangements] || room_arrangements;

  const PriceMaybe = props => {
    const {
      price,
      publicData,
      validListingTypes,
      intl,
      marketplaceCurrency,
      showCurrencyMismatch = false,
    } = props;
    const { listingType, unitType } = publicData || {};
    const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
    const showPrice = displayPrice(foundListingTypeConfig);
    const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
    const hasMultiplePriceVariants = publicData?.priceVariants?.length > 1;

    if (!showPrice || !price || (isPriceVariationsInUse && hasMultiplePriceVariants)) {
      return null;
    }

    const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);
    const priceValue = <span className={css.priceValue}>{formatMoney(intl, price)}</span>;
    const pricePerUnit = (
      <span className={css.perUnit}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </span>
    );

    return showCurrencyMismatch ? (
      <div className={css.priceContainerInCTA}>
        <div className={css.priceValueInCTA} title={priceTitle}>
          <FormattedMessage
            id="OrderPanel.priceInMobileCTA"
            values={{ priceValue: formattedPrice }}
          />
        </div>
        <div className={css.perUnitInCTA}>
          <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
        </div>
      </div>
    ) : (
      <div className={css.priceContainer}>
        <p className={css.price}>
          <FormattedMessage id="OrderPanel.price" values={{ priceValue, pricePerUnit }} />
        </p>
      </div>
    );
  };

  const titleClasses = classNames(titleClassName || css.orderTitle);
  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType, about_your_accommodation } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  const showClosedListingHelpText = listingConfig.id && isClosed;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
  const sortedListingFields = [...listingConfig.listingFields]
    ?.filter(field => fieldKeys.includes(field.key))
    ?.sort((a, b) => {
      const order = {
        room_arrangements: 0,
        activities_included: 1,
        bathroom_facilities: 2,
        meals_kitchen: 3,
        power_lighting: 4,
        laundry_cleaning: 5,
      };
      return order[a.key] - order[b.key];
    });

  const favIds = currentUser?.attributes.profile.privateData.favorites ?? [];

  const handlePopup = e => {
    e.preventDefault();
    setOpenPopup(prev => !prev);
  };

  const handleShare = () => {
    const slug = currentListing?.attributes?.publicData?.slug;
    const url = `${window.location.origin}/l/${slug || currentListing.id.uuid}`;

    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Merge all options with their parent field reference
  const allEnumOptions = sortedListingFields.flatMap(field =>
    (field.enumOptions || []).map(opt => ({
      ...opt,
      parentKey: field.key,
      parentField: field,
    }))
  );
  const limit = 8;
  const limitedEnumOptions = allEnumOptions.slice(0, limit);
  const totalLength = allEnumOptions.length;

  // Convert limitedEnumOptions to fields for the "Offerings" section
  function convertLimitedEnumOptions(limitedEnumOptions) {
    const groupedOptions = limitedEnumOptions.reduce((acc, item) => {
      const { parentKey, label, option, parentField } = item;
      if (!acc[parentKey]) {
        acc[parentKey] = {
          key: parentKey,
          scope: parentField.scope,
          schemaType: parentField.schemaType,
          enumOptions: [],
          filterConfig: parentField.filterConfig,
          showConfig: parentField.showConfig,
          saveConfig: parentField.saveConfig,
          categoryConfig: parentField.categoryConfig,
        };
      }
      acc[parentKey].enumOptions.push({ label, option });
      return acc;
    }, {});
    return Object.values(groupedOptions);
  }

  const limitedEnumOptionsFields = convertLimitedEnumOptions(limitedEnumOptions);

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainContainer}>
            <div className={css.listingGallery}>
              <div className={css.cententHeading}>
                <div className={css.headingTilte}>
                  {title} <span className={css.roundIcon}>•</span> {sleepingArrangementLabel}
                </div>
                <div className={css.headingLinks}>
                  <div className={css.sharelink} onClick={handleShare}>
                    <BrandIconCard type="share" />
                    {copied ? 'Link copied!' : 'Share'}
                  </div>
                  <div
                    className={css.favlink}
                    onClick={e => {
                      e.preventDefault();
                      const updatedIds = favIds?.includes(params.id)
                        ? favIds?.filter(id => id !== params.id)
                        : [...favIds, params.id];
                      const data = {
                        privateData: {
                          favorites: updatedIds,
                        },
                      };
                      onUpdateFavorites(data);
                    }}
                  >
                    <BrandIconCard type="favourite" />
                    {!favIds?.includes(params?.id) ? 'Add to favorites' : 'Remove from favourites'}
                  </div>
                </div>
              </div>
              <div>
                {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                  <ActionBarMaybe
                    className={css.actionBarForProductLayout}
                    isOwnListing={isOwnListing}
                    listing={currentListing}
                    showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                    currentUser={currentUser}
                  />
                ) : null}
                {mounted && currentListing.id ? (
                  <ActionBarMaybe
                    className={css.actionBarForProductLayout}
                    isOwnListing={isOwnListing}
                    listing={currentListing}
                    currentUser={currentUser}
                    editParams={{
                      id: listingId.uuid,
                      slug: listingSlug,
                      type: listingPathParamType,
                      tab: listingTab,
                    }}
                  />
                ) : null}
                <SectionGallery
                  listing={currentListing}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                />
              </div>
            </div>
            <div className={css.contentWrapper}>
              <div className={css.mainColumnForProductLayout}>
                <div className={css.mobileHeading}>
                  <H4 as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H4>
                </div>
                <div className={css.orderHeading}>
                  {titleDesktop ? titleDesktop : <H2 className={css.mainTitle}>{title}</H2>}
                  {description ? <div className={css.orderHelp}>{description}</div> : null}
                </div>
                <div className={css.headingReview}>
                  <SectionReviews
                    reviews={reviews}
                    uperReview={true}
                    fetchReviewsError={fetchReviewsError}
                  />
                </div>
                <div className={css.headingPrice}>
                  <PriceMaybe
                    price={price}
                    publicData={publicData}
                    validListingTypes={validListingTypes}
                    intl={intl}
                    marketplaceCurrency={marketplaceCurrency}
                  />
                </div>
                <div className={css.sectionTextDescription}>
                  <SectionTextMaybe
                    text={description}
                    accommodation={about_your_accommodation}
                    showAsIngress
                  />
                </div>

                {limitedEnumOptions.length > 0 ? (
                  <>
                    <div className={css.offeringsSection}>
                      <H2 rootClassName={css.sectionTitle}>Offerings</H2>

                      <div className={css.customListingFields}>
                        <CustomListingFields
                          publicData={publicData}
                          metadata={metadata}
                          listingFieldConfigs={limitedEnumOptionsFields}
                          categoryConfiguration={config.categoryConfiguration}
                          intl={intl}
                        />
                      </div>
                    </div>
                    {totalLength > limit && (
                      <div className={css.showAllBtn}>
                        <Button onClick={handlePopup}>Show All Amenities</Button>
                      </div>
                    )}
                    {totalLength > limit && openPopup && (
                      <div className={css.popUpModal}>
                        <div className={`${css.categoriesWrapper} ${css.modalContent}`}>
                          <div className={`${css.textcontainer} ${css.stickHeading}`}>
                            <H2>All Amenities</H2>
                            <span onClick={handlePopup} className={css.closeBtn}>
                              <BrandIconCard type="close" />
                            </span>
                          </div>
                          <div className={css.customListingFields}>
                            <CustomListingFields
                              publicData={publicData}
                              metadata={metadata}
                              listingFieldConfigs={sortedListingFields}
                              categoryConfiguration={config.categoryConfiguration}
                              intl={intl}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
              <div className={css.rightOrderPanel}>
                <div className={css.orderColumnForProductLayout}>
                  <OrderPanel
                    className={css.productOrderPanel}
                    listing={currentListing}
                    isOwnListing={isOwnListing}
                    onSubmit={handleOrderSubmit}
                    currentUser={currentUser}
                    authorLink={
                      <NamedLink
                        className={css.authorNameLink}
                        name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                        params={params}
                        to={{ hash: '#author' }}
                      >
                        {authorDisplayName}
                      </NamedLink>
                    }
                    title={
                      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                    }
                    titleDesktop={
                      <H4 as="h1" className={css.orderTitle}>
                        <FormattedMessage
                          id="ListingPage.orderTitle"
                          values={{ title: richTitle }}
                        />
                      </H4>
                    }
                    payoutDetailsWarning={payoutDetailsWarning}
                    author={ensuredAuthor}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onContactUser={onContactUser}
                    {...restOfProps}
                    validListingTypes={config.listing.listingTypes}
                    marketplaceCurrency={config.currency}
                    dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                    marketplaceName={config.marketplaceName}
                  />
                </div>
                <div className={css.orderBottom}>
                  <div className={css.contactText}>
                    If you need some help with your booking you can contact us here:
                  </div>
                  <div className={css.contactButton}>
                    <button className={css.whatsappButton}>
                      <a
                        href="https://wa.me/6798639060?text=Hi%2C%20I'm%20interested%20in%20booking%20a%20room%20at%20your%20hotel.%20Can%20you%20please%20share%20more%20details%3F"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <BrandIconCard type="whatsappwhiteicon" /> WhatsApp
                      </a>
                    </button>
                    <button className={css.messageButton}>
                      <BrandIconCard type="message" /> Message
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={css.relatedListingsWrapper}>
            <div className={css.mainContainer}>
              <div className={css.bottomSection}>
                <RelatedListing
                  realatedList={true}
                  relatedListingsReviews={relatedListingsReviews}
                  listings={featuredListings}
                  loading={relatedListingsError}
                  handleToggleFavorites={handleToggleFavorites}
                  onUpdateFavorites={onUpdateFavorites}
                  location={location}
                  currentUser={currentUser}
                />
              </div>
            </div>
          </div>
          <div className={css.mainContainer}>
            <div className={css.contentWrapper}>
              <div className={css.bottomSection}>
                <SectionMapMaybe
                  geolocation={geolocation}
                  publicData={publicData}
                  listingId={currentListing.id}
                  mapsConfig={config.maps}
                  config={config}
                />
                <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
                <SectionAuthorMaybe
                  title={title}
                  listing={currentListing}
                  authorDisplayName={authorDisplayName}
                  onContactUser={onContactUser}
                  isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                  onCloseInquiryModal={() => setInquiryModalOpen(false)}
                  sendInquiryError={sendInquiryError}
                  sendInquiryInProgress={sendInquiryInProgress}
                  onSubmitInquiry={onSubmitInquiry}
                  currentUser={currentUser}
                  onManageDisableScrolling={onManageDisableScrolling}
                  listingpage={true}
                  reviews={reviews}
                />
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    relatedListings,
    relatedListingsError,
    featuredListingsIds,
    relatedListingsReviews,
  } = state.ListingPage;
  const { currentUser } = state.user;
  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };
  const featuredListings = getListingsById(state, featuredListingsIds);

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    relatedListings,
    relatedListingsError,
    featuredListings,
    relatedListingsReviews,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)),
  onUpdateFavorites: payload => dispatch(updateProfile(payload)),
  onFetchlistingReviews: listingId => dispatch(fetchReviews(listingId)),
});

const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;